import * as React from "react";
import ISelectDropdownProps from "./ISelectDropdown";
import Label from "../../components/Label";
import ErrorMessage from "../../components/ErrorMessage";
import "../../../assets/sass/widgets/dropDownWidget.scss";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as dynamicFormActionCreators from "../../components/DynamicForm/actionCreators";
import { validationCheck } from "../../utils/validation";
import { checkForReadOnly, isInViewport, imagePathurl } from "../../utils/utils";
import * as CONFIG from "../../../config.json";

enum KEYPRESS {
    DOWN = "ArrowDown",
    UP = "ArrowUp",
    ESC = "Escape",
    ENTER = "Enter",
}

interface ISelectDropdownStateProps {
    isDropDown?: any;
    dropDownValue?: any;
    dropdownImg?: any;
    error?: any;
    cursor?: number;
    count?: number;
    searchIndexValue: string;
}

/**
 * @class DropDown
 * @category Widgets
 * @description Component is used to show the custom dropdown with images in the list and handles the keyboard actions for selecting the drop donw items , enables the searchable option to the user where user can type first few
 * characters of the item , to select the item
 */
class DropDown extends React.Component<ISelectDropdownProps, ISelectDropdownStateProps> {
    private keyPressTimer: NodeJS.Timeout = null;

    private readonly dropDownRef: React.LegacyRef<HTMLUListElement>;

    constructor(props) {
        super(props);
        this.dropDownRef = React.createRef();
        this.state = {
            isDropDown: false,
            dropDownValue: "",
            dropdownImg: "",
            cursor: -1,
            count: 0,
            error: { error: false, errorMessage: [] },
            searchIndexValue: "",
        };
        this.toggleDropDown = this.toggleDropDown.bind(this);
        this.hideDropDown = this.hideDropDown.bind(this);
        this.getActiveItemByKey = this.getActiveItemByKey.bind(this);
        this.onDropDownChange = this.onDropDownChange.bind(this);
        this.blurCheck = this.blurCheck.bind(this);
    }

    toggleDropDown() {
        const { isDropDown, dropDownValue } = this.state;
        const { schema } = this.props;
        this.setState({ isDropDown: !isDropDown, cursor: -1 }, () => {
            // const { removeFieldIdFromStore } = this.props.dynamicFormActions;
            // removeFieldIdFromStore(this.props.id);
            if (dropDownValue) {
                const element: HTMLElement = document.getElementById(dropDownValue);
                if (element) {
                    const cursor = schema.anyOf.findIndex((option) => option.title === dropDownValue);
                    this.setState({ cursor, count: cursor * 40 });
                    element.parentElement.scrollTop = element.offsetTop;
                }
            }

            // if dropdown not completely show in viewport, scrollTo viewport
            if (this.state.isDropDown) {
                const current = this.dropDownRef["current"];
                if (!isInViewport(current)) {
                    current.scrollIntoView({
                        behavior: "auto",
                        block: "center",
                        inline: "center",
                    });
                }
            }
        });
    }

    hideDropDown() {
        this.setState({ isDropDown: false, count: 0, cursor: -1 }, () => {
            this.blurCheck(this.state.dropDownValue);
        });
        document.getElementsByTagName("body")[0].style.overflow = "scroll";
    }

    /**
     * Once user stop type, search the relevant result and
     * selected from dropdown
     */
    chooseOptionBySearch = () => {
        const { schema } = this.props;
        const { searchIndexValue, dropDownValue } = this.state;
        let { count, cursor } = this.state;
        if (searchIndexValue) {
            const filteredOption = schema.anyOf.filter(
                (option) => option.title.toLowerCase().indexOf(this.state.searchIndexValue.toLowerCase()) === 0
            );
            let optionIndex = -1;
            if (filteredOption.length) {
                if (dropDownValue && filteredOption.length > 1) {
                    const existIndex = filteredOption.findIndex((option) => option.title === dropDownValue);
                    optionIndex = filteredOption.length - 1 < existIndex + 1 ? 0 : existIndex + 1;
                } else {
                    optionIndex = 0;
                }
                const matchOption = filteredOption[optionIndex];
                if (matchOption) {
                    this.onDropDownChange(matchOption.title, matchOption.enum[0]);
                    this.blurCheck(matchOption.title);
                    const element: HTMLElement = document.getElementById(this.state.dropDownValue);
                    if (element) {
                        element.parentElement.scrollTop = element.offsetTop;
                    }
                    cursor = schema.anyOf.findIndex((option) => option.title === matchOption.title);
                    count = cursor * 40;
                }
            }
        }
        this.setState({ searchIndexValue: "", cursor, count });
    };

    /**
     * Open DropDown And ScrollTo highlight element
     * @param {number} count
     * @param {number} cursor
     */
    openDropDownAndScrollTo = (count: number, cursor: number) => {
        this.setState({ isDropDown: true, count, cursor }, () => {
            document.getElementsByTagName("body")[0].style.overflow = "hidden";
            this.dropDownRef["current"].scrollTo(0, count);
        });
    };

    /**
     * Handle onKeyDown on dropdown list
     * store all the keydown value in component state until user stop typing
     * @param {KeyboardEvent} event
     */
    showDropDown = (event: any) => {
        let { count } = this.state;
        const { cursor, isDropDown } = this.state;
        const { schema } = this.props;
        const { key } = event;

        const dropDownOptionsLength = schema.anyOf.length;
        const downArrowKeyValue = dropDownOptionsLength * 40;

        if (Object.values(KEYPRESS).includes(key)) {
            if (key === KEYPRESS.ESC) {
                return this.hideDropDown();
            }
            if (key === KEYPRESS.DOWN) {
                if (!isDropDown) {
                    this.toggleDropDown();
                } else {
                    count = count + 40;
                    if (count <= downArrowKeyValue) {
                        this.openDropDownAndScrollTo(count, cursor + 1);
                    }
                }
            }
            if (key === KEYPRESS.UP) {
                if (isDropDown) {
                    count = count - 40;
                    if (count >= 0) {
                        this.openDropDownAndScrollTo(count, cursor - 1);
                    }
                }
            }
            if (key === KEYPRESS.ENTER && cursor !== -1) {
                const selectedOption = schema.anyOf[cursor];
                this.onDropDownChange(selectedOption.title, selectedOption.enum[0]);
                this.blurCheck(selectedOption.title);
                this.hideDropDown();
            }
        } else {
            clearTimeout(this.keyPressTimer);
            this.setState({ searchIndexValue: `${this.state.searchIndexValue}${key}` });
            this.keyPressTimer = setTimeout(this.chooseOptionBySearch, 500);
        }
    };

    blurCheck(value) {
        const { family } = this.props.schema.const;
        const { errorSchema } = this.props;
        const id = this.props.id.split("_");
        const fieldName = this.props.id.split("_")[id.length - 1];
        if (value === "- Select -") {
            this.setState({
                error: validationCheck("", fieldName, family, errorSchema),
            });
        } else {
            this.setState({
                error: validationCheck(value, fieldName, family, errorSchema),
            });
        }
    }

    getActiveItemByKey(key) {
        const obj = this.props.schema.anyOf.filter((item) => item.enum["0"] === key);
        return obj && obj.length > 0 && obj[0];
    }

    onDropDownChange(value, key) {
        let img;
        if (this.props.schema.anyOf[0].const && this.props.schema.anyOf[0].const.imgsrc) {
            const obj = this.getActiveItemByKey(key);
            img = obj && obj.const.imgsrc;
        }
        const imgPath = img && img.includes("Src") ? img.replace("Src", `${CONFIG["accessoriesImagePath"]}`) : img;
        this.setState({ dropDownValue: value, dropdownImg: imgPath });
        this.props.onChange(key);
        const currentId = this.props.id;
        if (this.props.schema.title.toLowerCase() == "country") {
            if (currentId.includes("mailWarrantyInfo")) {
                this.props.dynamicFormActions.getMailAddressCountryCode({ payload: key });
            } else {
                this.props.dynamicFormActions.getBuildingAddressCountryCode({ payload: key });
            }
        }
    }

    componentDidMount() {
        const { schema, buildingAddress, mailAddress, id } = this.props;
        let { value } = this.props;
        let title;
        let globalDataSource, globalKey;
        const { selectAddress } = this.props.dynamicFormActions;
        const defaultSetting =
            this.props.contractorSettings.contractorFormDefaults &&
            this.props.contractorSettings.contractorFormDefaults[id];
        const defaultValue = defaultSetting || schema.default;
        if (typeof value === "object" && Object.keys(value).length == 0) {
            value = "";
        }
        if (id.includes("mailWarrantyInfo")) {
            globalDataSource = mailAddress;
            globalKey = "mailAddress";
        } else {
            globalDataSource = buildingAddress;
            globalKey = "buildingAddress";
        }
        // if previous value is provided, update the dropdown
        if ((typeof value === "string" || typeof value === "number") && (value || (defaultSetting && value == ""))) {
            if (defaultSetting && value == "") {
                title = this.getActiveItemByKey(defaultSetting).title;
                this.onDropDownChange(title, defaultSetting);
            } else {
                title = !value ? "- Select -" : this.getActiveItemByKey(value).title;
                title && title != "- Select -"
                    ? this.onDropDownChange(title, value)
                    : this.onDropDownChange("- Select -", "");
            }
            if (schema.title == "State/Territory" || schema.title == "Province") {
                globalDataSource.state = value;
                selectAddress({ key: globalKey, payload: globalDataSource, label: "state" });
            }
            // if there is no previous value & default value is provided, update the dropdown
        } else if (defaultValue) {
            title = this.getActiveItemByKey(defaultValue).title;
            {
                title ? this.onDropDownChange(title, defaultValue) : this.onDropDownChange("- Select -", "");
            }
        } else {
            if (schema.title == "State/Territory" || schema.title == "Province") {
                globalDataSource.state = value;
                selectAddress({ key: globalKey, payload: globalDataSource, label: "state" });
            }
            // preset "- Select -" if no previous or default value is provided
            this.onDropDownChange("- Select -", "");
        }
    }

    componentDidUpdate(prevProps: any, prevState: any) {
        let { value } = this.props;
        const { schema, buildingAddress, mailAddress, id, formData, adminData } = this.props;
        const { value: prevValue, schema: prevSchema, buildingAddress: prevBuildingAddress, mailAddress: prevMailAddress, formData: prevFormData } = prevProps;
        let globalDataSource, globalKey, prevGlobalDataSource;

        const prevWarrantyType = prevFormData?.projectDetails?.warrantyType;
        const currentWarrantyType = formData?.projectDetails?.warrantyType;

        /*const defaultSetting =
            this.props.contractorSettings.contractorFormDefaults &&
            this.props.contractorSettings.contractorFormDefaults[this.props.id];
        if (!this.state.isDefaultSet && defaultSetting) {
            const title = !value ? "- Select -" : this.getActiveItemByKey(defaultSetting).title;
            title && title != "- Select -" ? this.onDropDownChange(title, value) : this.onDropDownChange("- Select -", "");
            this.setState({ isDefaultSet: true });
        }*/

        if (id.includes("mailWarrantyInfo")) {
            globalDataSource = mailAddress;
            prevGlobalDataSource = prevMailAddress;
            globalKey = "mailAddress";
        } else {
            globalDataSource = buildingAddress;
            prevGlobalDataSource = prevBuildingAddress;
            globalKey = "buildingAddress";
        }
        if (schema.title != "State/Territory" && schema.title != "Province" && schema.title.toLowerCase() != "country") {
            if (typeof value === "object" && Object.keys(value).length == 0) {
                value = "";
            }
        }
        // on changing the dropdown value, update the dropdown
        let title;
        if (prevValue !== value) {
            if (typeof value === "object" && Object.keys(value).length == 0) {
                value = "";
            }
            title = !value ? "- Select -" : this.getActiveItemByKey(value).title;
            {
                title && title != "- Select -"
                    ? this.onDropDownChange(title, value)
                    : this.onDropDownChange("- Select -", "");
            }
            // const { removeFieldIdFromStore } = this.props.dynamicFormActions;
            // removeFieldIdFromStore(this.props.id);
            // Type Ahead changes
            const { selectAddress } = this.props.dynamicFormActions;
            if (schema.title == "State/Territory" || schema.title == "Province") {
                if (!!value || JSON.stringify(prevGlobalDataSource) != JSON.stringify(globalDataSource)){
                    globalDataSource.state = value;
                    selectAddress({ key: globalKey, payload: globalDataSource, label: "state" });
                }
            }
            if (schema.title.toLowerCase() == "country") {
                //clear all the fields when we change the country
                const address = {
                    street: "",
                    city: "",
                    zip: "",
                    state: "",
                    postalCode: "",
                    countryCode: value,
                };
                selectAddress({ key: globalKey, payload: address });
            }
        }
        // update dropdown value based on globalDataSource
        else if (
            schema.hasOwnProperty("const") &&
            schema.const &&
            schema.const.dataSource &&
            schema.title.toLowerCase() !== "country" &&
            globalDataSource &&
            value !== globalDataSource.state &&
            !(value && Object.keys(value).length === 0 && value.constructor === Object)
            // value !== {}
        ) {
            const key = globalDataSource.state;
            title = this.getActiveItemByKey(key).title;
            {
                title ? this.onDropDownChange(title, key) : this.onDropDownChange("- Select -", "");
            }
            this.props.onChange(key);
        }
        // if the dropdown list is not the same as previous list
        else if (
            prevSchema.anyOf.length !== schema.anyOf.length ||
            !prevSchema.anyOf.every((item) => schema.anyOf.some((elem) => elem.title === item.title))
        ) {
            // if the value exists in the current dropdown list, set that value
            if (this.getActiveItemByKey(value)) {
                title = this.getActiveItemByKey(value).title;
                this.onDropDownChange(title, value);
            } else {
                // if the value does not exist in the current dropdown list, set to default
                title = this.getActiveItemByKey(schema.default).title;
                {
                    const { removeFieldIdFromStore } = this.props.dynamicFormActions;
                    removeFieldIdFromStore(this.props.id);
                    // Delaying OnChange event, making sure ID is removed from store before OnChange.
                    setTimeout(() => {
                        title ? this.onDropDownChange(title, schema.default) : this.onDropDownChange("- Select -", "");
                    }, 500);
                    // title ? this.onDropDownChange(title, schema.default) : this.onDropDownChange("- Select -", "");
                }
            }
        }
        if (prevValue !== value) {
            this.setState(
                {
                    error: { error: false, errorMessage: [] },
                },
                () => {
                    if (value) {
                        const { removeFieldIdFromStore } = this.props.dynamicFormActions;
                        removeFieldIdFromStore(this.props.id);
                    }
                }
            );
        }
        
        // Removing previously selected value if it's discontinued for the selected warranty type.
        if ( prevWarrantyType !== currentWarrantyType && value) {
            const isAdmin = adminData?.userInfo?.isAdmin === true;
            const { removeFieldIdFromStore } = this.props.dynamicFormActions;

            if (value) {
                const selectedOption = schema.anyOf?.find(option => option.enum[0] === value);
                const isDiscontinued = selectedOption?.const?.IsDiscon === true;
                const shouldRemove = !isAdmin && isDiscontinued;
                if ( shouldRemove ) {
                    console.log(`Removing discontinued field: ${this.props.id}`);
                    removeFieldIdFromStore(this.props.id);
                    setTimeout(() => {
                        title ? this.onDropDownChange(title, schema.default) : this.onDropDownChange("- Select -", "");
                    }, 500);
                }
            }
        }

    }

    fieldClicked = () => {
        const { removeFieldIdFromStore } = this.props.dynamicFormActions;
        removeFieldIdFromStore(this.props.id);
    };

    render() {
        const { schema, disabled, className, value, id, isSubmittedForm, errorDetails, adminData } = this.props;
        const { isDropDown, dropDownValue, dropdownImg, cursor, error } = this.state;
        const isAdmin = adminData?.userInfo?.isAdmin === true;

        //image CDN_PATH
        const getimagePath = imagePathurl();

        let dropDownType = "normal";
        if (schema.const && schema.const.controlType && schema.const.controlType === "image") {
            dropDownType = "image";
        }
        const contractorColors = this.props.contractorSettings.canDisplayContractorColors || [];

        const dropDownOptions = schema.anyOf.filter((option, index) => {
            if (option.const && option.const.IsCSColor && option.const.IsCSColor.toString() === "true") {
                if (!!contractorColors.length) {
                    return contractorColors.includes(parseInt(option.enum[0]));
                }
                return false;
            }
            // Hide option when IsDiscon is true and the user is not an Admin
            if (!isAdmin && option?.const?.IsDiscon ) {
                return false;
            }
            return true;
        });
        // console.log(`dropdownOptions--`, dropDownOptions)
        // Removing class for ShingleColor for ArmorPledge only to create margin bottom
        if(document.getElementById('root_residentialWarrantyForm_projectDetails_warrantyType')) {
            if((document.getElementsByClassName('dropdown-selection') as any)[0].innerHTML.trim() == "ArmorPledge™") {
                const shingleClass = document.getElementsByClassName(("form-group field field-string  shingleColor mb0") as any);
                if(shingleClass.length > 0) {
                    document.getElementsByClassName(("form-group field field-string  shingleColor mb0") as any)[0].classList.remove('mb0'); 
                }
            }
        }

        return (
            <>
                <Label schema={schema} />
                <div
                    id={id}
                    className="gaf-dropdown-container"
                    onClick={checkForReadOnly(schema.const, isSubmittedForm) ? () => {} : this.fieldClicked}
                >
                    <div
                        className={
                            (this.state.error && this.state.error.error && this.state.error.errorMessage.length > 0) ||
                            (this.props.errorDetails[id] && this.props.errorDetails[id].length > 0)
                                ? "error-boundry gaf-dropdown"
                                : "gaf-dropdown"
                        }
                        tabIndex={schema.const.tabIndex || 0}
                        onClick={checkForReadOnly(schema.const, isSubmittedForm) ? () => {} : this.toggleDropDown}
                        onBlur={checkForReadOnly(schema.const, isSubmittedForm) ? () => {} : this.hideDropDown}
                        onKeyDown={checkForReadOnly(schema.const, isSubmittedForm) ? () => {} : this.showDropDown}
                    >
                        <img src={`${getimagePath}chevron-down.png`} className="chevron-down-thickred" />
                        {dropDownType === "image" && dropDownValue !== "- Select -" && (
                            <img
                                src={`${dropdownImg !== null ? dropdownImg : `${getimagePath}no_color.png`}`}
                                className="active-dropdown-img"
                            />
                        )}
                        <p
                            className={
                                dropDownType === "image" && dropDownValue !== "- Select -"
                                    ? "dropdown-selection-withimg"
                                    : "dropdown-selection"
                            }
                            title={dropDownValue}
                        >
                            {dropDownValue}
                        </p>
                        {isDropDown && (
                            <ul
                                ref={this.dropDownRef}
                                className={dropDownType === "image" ? "dropdown-img-list" : "dropdown-list"}
                            >
                                <li
                                    key="select"
                                    className={dropDownType === "image" ? "img-dropdown-select" : ""}
                                    onClick={(e) => {
                                        this.onDropDownChange("- Select -", "");
                                        this.blurCheck("- Select -");
                                    }}
                                >
                                    - Select -
                                </li>
                                {dropDownOptions.map((option, index) => {
                                    const imgPath =
                                        (option.const &&
                                            (option.const.imgsrc && option.const.imgsrc.includes("Src")
                                                ? option.const.imgsrc.replace(
                                                      "Src",
                                                      `${CONFIG["accessoriesImagePath"]}`
                                                  )
                                                : option.const.imgsrc)) ||
                                        "";
                                    return (
                                        <li
                                            key={index}
                                            id={option.title}
                                            className={cursor === index ? "highlight" : null}
                                            onClick={(e) => {
                                                this.onDropDownChange(option.title, option.enum[0]);
                                                this.blurCheck(option.title);
                                            }}
                                        >
                                            {imgPath || dropDownType === "image" ? (
                                                <img
                                                    className="li-dropdown-img"
                                                    alt={imgPath}
                                                    src={`${
                                                        imgPath !== null ? imgPath : `${getimagePath}no_color.png`
                                                    }`}
                                                />
                                            ) : null}
                                            {option.title}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                    {schema.const && schema.const.fieldSideLabel && (
                        <div className="fieldside-label">{schema.const.fieldSideLabel}</div>
                    )}
                </div>
                <ErrorMessage errorDetails={errorDetails} errorState={error} propId={id} />
            </>
        );
    }
}
export function mapStateToProps(state) {
    return {
        buildingAddress: state.dynamicFormInfo.global.buildingAddress,
        mailAddress: state.dynamicFormInfo.global.mailAddress,
        errorDetails: state.dynamicFormInfo.errorDetails || {},
        isSubmittedForm: state.adminData.isSubmittedForm,
        contractorSettings: state.dynamicFormInfo.contractorSettings || {},
        adminData: state.adminData,
        formData: state.dynamicFormInfo.warrantyInfo.residentialWarrantyForm,
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        dynamicFormActions: bindActionCreators(dynamicFormActionCreators, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(DropDown);
